// @flow

import React, { type Node } from 'react';
import { getQueryParamValue } from '@tectonics/utils/url';
import { getSafeCountryFromCode, CountryProvider } from '@tectonics/commons/country';
import { getTenantFromCode, TenantProvider } from '@tectonics/commons/tenant';
import { GlobalStyle } from '@tectonics/look-and-feel/volcanes/commons/global-style';
import { VolcanesThemeProvider, getThemeById } from '@tectonics/look-and-feel/volcanes/theme';
import { PaymentProvider, usePayment } from './context';
import { usePaymentQuery } from './hooks/use-payment-query';
import { useApprovalPolling } from './hooks/use-approval-polling';
import { usePaymentState } from './hooks/use-payment-state';
import { PageContainer } from './controller.styles';
import { Footer } from './components/footer';
import { ErrorScreen } from './screens/error-screen';
import { LoadingScreen } from './screens/loading-screen';
import { PhoneInputScreen } from './screens/phone-input-screen';
import { SuccessScreen } from './screens/success-screen';
import { TimeoutScreen } from './screens/timeout-screen';
import { WaitingApprovalScreen } from './screens/waiting-approval-screen';

type Props = {|
  +tenantCode: ?string,
|};

export function YapeHostedPaymentController({ tenantCode }: Props): Node {
  if (!tenantCode) {
    throw new Error('URL query param "tenant" not found');
  }

  const hash = getQueryParamValue('hash');
  if (!hash) {
    throw new Error('URL query param "hash" not found');
  }

  const tenant = getTenantFromCode(tenantCode);
  const country = getSafeCountryFromCode('pe');
  const theme = getThemeById('default');

  return (
    <TenantProvider value={tenant}>
      <CountryProvider value={country}>
        <VolcanesThemeProvider theme={theme}>
          <>
            <GlobalStyle />
            <HostedPaymentRoot paymentHash={hash} />
          </>
        </VolcanesThemeProvider>
      </CountryProvider>
    </TenantProvider>
  );
}

type HostedPaymentRootProps = {|
  +paymentHash: string,
|};

const SCREEN_BY_STATE: {|
  +LOADING: () => Node,
  +SUBMITTING: () => Node,
  +COLLECT_PHONE: () => Node,
  +WAITING_APPROVAL: () => Node,
  +AWAITING_PAYMENT_STATUS: () => Node,
  +TIMEOUT: () => Node,
  +SUCCESS: () => Node,
  +ERROR: () => Node,
|} = {
  LOADING: LoadingScreen,
  SUBMITTING: LoadingScreen,
  COLLECT_PHONE: PhoneInputScreen,
  WAITING_APPROVAL: WaitingApprovalScreen,
  AWAITING_PAYMENT_STATUS: LoadingScreen,
  TIMEOUT: TimeoutScreen,
  SUCCESS: SuccessScreen,
  ERROR: ErrorScreen,
};

function HostedPaymentRoot({ paymentHash }: HostedPaymentRootProps): Node {
  const [data, dispatch] = usePaymentState(paymentHash);
  const value = React.useMemo(() => ({ data, dispatch }), [data, dispatch]);

  return (
    <PaymentProvider value={value}>
      <PageContainer>
        <HostedPaymentScreen />
        <Footer />
      </PageContainer>
    </PaymentProvider>
  );
}

function HostedPaymentScreen(): Node {
  usePaymentQuery();
  useApprovalPolling();
  const { data } = usePayment();
  const Screen = SCREEN_BY_STATE[data.state];

  return <Screen />;
}
